import axios from 'axios';
import { api } from '../../../config';

/**
 * Pemanggilan API publik Pengumuman Tender. Tanda tangan fungsi dan nama
 * field dipertahankan identik dengan seam mockup sebelumnya (lihat
 * `issue-FE-public-tender-announcement-mockup.md`), sehingga komponen
 * halaman (`index.js`, `Detail.js`) tidak perlu disentuh.
 *
 * PENTING: instance axios dibuat terpisah, TANPA interceptor penambah
 * token — halaman ini terbuka ke publik tanpa login dan tidak boleh
 * pernah mengirim header Authorization.
 */
const publicClient = axios.create({
  baseURL: api.API_URL,
});

// Empat nilai status tersimpan sebagai PascalCase tanpa spasi (D-33); label
// berspasi dibentuk lewat kamus terjemahan.
//
// `Draft` sengaja TIDAK ada di sini: barisnya tidak pernah tampil ke publik
// (D-43), jadi menyediakannya sebagai pilihan filter hanya menghasilkan
// halaman kosong.
export const PUBLIC_STATUS = {
  ANNOUNCED: 'Announced',
  BIDDING_OPEN: 'BiddingOpen',
  BIDDING_CLOSED: 'BiddingClosed',
};

// Tab kategori (D-23). Nilai `category` dikirim apa adanya ke API; `null`
// berarti tab "Semua Pengadaan" dan paramnya tidak dikirim sama sekali.
export const PROCUREMENT_CATEGORY = {
  PRODUCT: 'Product Hydro',
  CRUDE: 'Crude & Condensate',
};

export const CATEGORY_TABS = [
  { key: 'all', category: null },
  { key: 'product', category: PROCUREMENT_CATEGORY.PRODUCT },
  { key: 'crude', category: PROCUREMENT_CATEGORY.CRUDE },
];

/**
 * Meniru GET /api/public/tender-announcement
 */
export const fetchTenderAnnouncements = async ({
  search = '',
  announcementDateFrom = null,
  announcementDateTo = null,
  status = null,
  category = null,
  sortBy = null,
  sortDir = null,
  pageNumber = 1,
  pageSize = 10,
  language = 'id',
} = {}) => {
  const params = {
    PageNumber: pageNumber,
    PageSize: pageSize,
    Lang: language,
  };
  if (search) params.Search = search;
  if (announcementDateFrom) params.AnnouncementDateFrom = announcementDateFrom;
  if (announcementDateTo) params.AnnouncementDateTo = announcementDateTo;
  if (status) params.Status = status;
  if (category) params.Category = category;
  // Pengurutan ditangani server: tabelnya dipaginasi, jadi mengurutkan di klien
  // hanya menata ulang satu halaman.
  if (sortBy) params.SortBy = sortBy;
  if (sortDir) params.SortDir = sortDir;

  const response = await publicClient.get('/public/tender-announcement', {
    params,
  });
  const payload = response?.data?.data || response?.data || {};

  return {
    items: payload.items || [],
    totalCount: payload.totalItems ?? 0,
    pageNumber: payload.pageNumber ?? pageNumber,
    pageSize: payload.pageSize ?? pageSize,
  };
};

/**
 * Meniru GET /api/public/tender-announcement/{id}
 * Mengembalikan null bila tidak ditemukan (halaman menampilkan "tidak ditemukan").
 */
export const fetchTenderAnnouncementById = async (id, language = 'id') => {
  try {
    const response = await publicClient.get(
      `/public/tender-announcement/${id}`,
      {
        params: { lang: language },
      }
    );
    return response?.data?.data || response?.data || null;
  } catch (error) {
    if (error?.response?.status === 404) return null;
    throw error;
  }
};

/**
 * Ringkasan untuk chip di hero. Dihitung dari SELURUH data yang lolos gate
 * publik, bukan hasil filter, sehingga angkanya tetap saat pengunjung
 * mengubah filter.
 */
export const fetchTenderAnnouncementSummary = async () => {
  const response = await publicClient.get(
    '/public/tender-announcement/summary'
  );
  const payload = response?.data?.data || response?.data || {};
  return {
    activeCount: payload.activeCount ?? 0,
    finishedCount: payload.finishedCount ?? 0,
  };
};
